import { SectionId } from "@eventr/common";
import type { BoundStore } from "../bound.store";

// The create-itinerary flow holds `selectedItinID` at -1 while its form is open, so the
// form's draft is keyed apart from every saved itinerary. This flag marks that -1 as
// deliberate, and the itinerary auto-selection leaves it alone while it is set. Module
// state, never persisted: a reload drops it and the selection recovers by itself.
let creating = false;
// The itinerary selected when "Add new itinerary" was chosen, to return to if the form is
// left without saving. Only the entry point sets it: the form's own providers reopen the
// flag on every sub screen and must not overwrite it with the -1 they see.
let returnTo = -1;

// The General draft the form seeded the new itinerary with. A new itinerary has no saved
// section to compare its draft against, so this is what "unchanged" means. Lost on reload,
// where a persisted draft then counts as changed, the safe way round.
let seed: object | null = null;

/** Records the draft the create form seeded, see {@link newItinerarySeed}. */
export const recordNewItinerarySeed = (item: object) => {
  seed = item;
};

/** The seeded General draft of the itinerary being created, or null when unknown. */
export const newItinerarySeed = () => seed;

/** Opens the create-itinerary flow, remembering `from` as the itinerary to return to. */
export const beginItineraryCreation = (from?: number) => {
  creating = true;
  if (from !== undefined) returnTo = from;
};

/** Closes it, on save, on leaving the form, or when any itinerary is selected. */
export const endItineraryCreation = () => {
  creating = false;
};

/** True while the create-itinerary form owns the selection. */
export const isCreatingItinerary = () => creating;

/**
 * Reselects an itinerary after the create form closed without saving. Nothing else does:
 * the auto-selection only runs when the itinerary map changes, so the selection would sit
 * at -1 and Home would show its skeleton until a pull to refresh. Returns to the itinerary
 * the flow was opened from, or the usual default when that one is gone.
 *
 * Call it once the form has unmounted and the next screen has mounted, since a sub screen
 * of the flow reopens it on mount; a save has already selected the new itinerary.
 */
export const restoreAfterAbandonedCreation = (
  store: Pick<BoundStore, "selectedItinID" | "itineraryMap" | "setCurrentItin" | "resolveItinId">,
) => {
  if (creating || store.selectedItinID !== -1) return;
  if (store.itineraryMap.some((itin) => itin.id === returnTo)) {
    void store.setCurrentItin(returnTo);
  } else {
    void store.resolveItinId();
  }
};

/**
 * The General section's staged values for the selected itinerary, under the sentinel id
 * selectEditItem uses for that section. No fallback to a saved section: a new itinerary
 * exists only as its draft, and the saved section belongs to whichever itinerary was
 * selected before the create flow started.
 *
 * Returns the stored draft itself, not a copy, so the reference stays stable between
 * renders.
 */
export const selectGeneralDraft = (state: Pick<BoundStore, "draftSectionItems" | "selectedItinID">) =>
  state.draftSectionItems[state.selectedItinID]?.[SectionId.GENERAL]?.find((item) => item._id === "0") ?? null;
